/**
 * VisibilityToggle Component - Public/private toggle with confirmation
 */

import React, { useState } from 'react'
import { Card, Switch, Button, Modal } from '../ui'
import { Globe, Lock, AlertTriangle, Check } from 'lucide-react'

const VisibilityToggle = ({
  isPublic = false,
  onToggle,
  onConfirm,
  className = '',
  ...props
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [pendingState, setPendingState] = useState(null)

  const handleToggle = () => {
    const newState = !isPublic
    setPendingState(newState)
    setShowConfirmation(true)
  }

  const handleConfirm = () => {
    setShowConfirmation(false)
    onToggle?.(pendingState)
    onConfirm?.(pendingState)
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    setPendingState(null)
  }

  return (
    <>
      <Card className={`p-6 ${className}`} {...props}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`
              w-12 h-12 rounded-lg flex items-center justify-center
              ${isPublic ? 'bg-success-soft' : 'bg-neutral-100'}
            `}>
              {isPublic ? (
                <Globe size={20} className="text-success" />
              ) : (
                <Lock size={20} className="text-neutral-400" />
              )}
            </div>
            <div>
              <h4 className="text-sm font-semibold text-ink">
                {isPublic ? 'Publicly Listed' : 'Private Mode'}
              </h4>
              <p className="text-xs text-muted mt-0.5">
                {isPublic 
                  ? 'Your company is visible on the Discover directory' 
                  : 'Your company is hidden from the public directory'
                }
              </p>
            </div>
          </div>

          <Switch
            checked={isPublic}
            onChange={handleToggle}
            size="lg"
            aria-label="Toggle visibility"
          />
        </div>

        {isPublic && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              Your company name, industry, country, and governance score will be visible to all visitors. 
              Sensitive documents remain gated behind access requests.
            </p>
          </div>
        )}
      </Card>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmation}
        onClose={handleCancel}
        title={pendingState ? 'Make Company Public?' : 'Make Company Private?'}
        description={pendingState 
          ? 'Making your company public will expose your name, industry, country, and governance score to all visitors on the Bizos Discover directory. Sensitive documents remain gated behind access requests.' 
          : 'Making your company private will remove it from the public Discover directory. Existing investor tokens will remain active until revoked.'
        }
        size="sm"
        footer={
          <div className="flex gap-3 w-full">
            <Button variant="secondary" onClick={handleCancel} fullWidth>
              Cancel
            </Button>
            <Button 
              variant={pendingState ? 'primary' : 'danger'} 
              onClick={handleConfirm}
              fullWidth
              icon={pendingState ? <Check size={16} /> : <Lock size={16} />}
            >
              {pendingState ? 'Confirm & Make Public' : 'Confirm & Make Private'}
            </Button>
          </div>
        }
      >
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle size={18} className="text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800">
            This change will take effect immediately and affect how investors discover your company.
          </p>
        </div>
      </Modal>
    </>
  )
}

VisibilityToggle.displayName = 'VisibilityToggle'

export default VisibilityToggle